<template>
  <div>
    <Head title="Agrarwetter" />

    <div class="mb-6">
      <p class="text-sm text-gray-500 uppercase tracking-wide">Agrarwetter</p>
      <h1 class="text-2xl font-bold text-gray-900">{{ location }}</h1>
    </div>

    <!-- Warning banners -->
    <div v-if="warnings.length" class="mb-6 space-y-2">
      <div
        v-for="(warning, i) in warnings"
        :key="i"
        class="flex items-start justify-between rounded-lg px-5 py-4 border"
        :style="warningBannerStyle(warning)"
      >
        <div class="flex items-center gap-3">
          <span class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: warning.color }" />
          <div>
            <p class="font-semibold">{{ warning.headline }}</p>
            <p class="text-sm opacity-80">{{ warning.description }}</p>
          </div>
        </div>
        <span class="text-xs font-semibold uppercase tracking-wide opacity-70 ml-4 flex-shrink-0">DWD Warnung</span>
      </div>
    </div>

    <!-- Green banner when no warnings -->
    <div v-else class="mb-6 flex items-center gap-3 rounded-lg px-5 py-4 border border-green-300 bg-green-50 text-green-800">
      <span class="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" />
      <span class="font-semibold">Keine aktuellen DWD-Warnungen für diese Region</span>
    </div>

    <!-- Hourly readings timeline -->
    <div v-if="readings.length" class="bg-white rounded-xl shadow p-6 mb-6">
      <h2 class="text-lg font-semibold text-gray-800 mb-4">Verlauf (stündlich)</h2>
      <div class="overflow-x-auto pb-2">
        <div class="flex gap-3" style="width: max-content;">
          <div
            v-for="reading in readings"
            :key="reading.id"
            class="flex-shrink-0 w-44 rounded-xl border p-3 text-xs"
            :class="reading.warnings_max_level ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'"
          >
            <!-- Time header (top-left) -->
            <div class="mb-2">
              <div class="text-gray-500 text-xs">{{ readingFullDate(reading.recorded_at) }}</div>
              <div class="font-semibold text-gray-700">{{ readingWeekday(reading.recorded_at) }}, {{ readingTime(reading.recorded_at) }}</div>
            </div>

            <!-- Weather values -->
            <table class="w-full">
              <tbody>
                <tr>
                  <td class="text-gray-500 pr-1">🌡️</td>
                  <td class="font-semibold text-right">{{ readingVal(reading.temperature, '°C') }}</td>
                </tr>
                <tr>
                  <td class="text-gray-500 pr-1">💧</td>
                  <td class="font-semibold text-right">{{ readingVal(reading.humidity, '%') }}</td>
                </tr>
                <tr>
                  <td class="text-gray-500 pr-1">🌀</td>
                  <td class="font-semibold text-right">{{ readingVal(reading.pressure, 'hPa') }}</td>
                </tr>
                <tr>
                  <td class="text-gray-500 pr-1">💨</td>
                  <td class="font-semibold text-right">{{ readingVal(reading.windspeed, 'km/h') }}</td>
                </tr>
                <tr>
                  <td class="text-gray-500 pr-1">👁️</td>
                  <td class="font-semibold text-right">{{ readingVal(reading.visibility_km, 'km') }}</td>
                </tr>
                <tr>
                  <td class="text-gray-500 pr-1">🌱</td>
                  <td class="font-semibold text-right">{{ readingVal(reading.soil_temperature, '°C') }}</td>
                </tr>
                <tr>
                  <td class="text-gray-500 pr-1">🪣</td>
                  <td class="font-semibold text-right">{{ readingVal(reading.soil_moisture_pct, '%') }}</td>
                </tr>
                <tr>
                  <td class="text-gray-500 pr-1">☀️</td>
                  <td class="font-semibold text-right">{{ readingVal(reading.radiation, 'W/m²') }}</td>
                </tr>
                <tr>
                  <td class="text-gray-500 pr-1">💧ET₀</td>
                  <td class="font-semibold text-right">{{ readingVal(reading.et0, 'mm') }}</td>
                </tr>
              </tbody>
            </table>

            <!-- Warning info with legend label -->
            <div class="mt-2 pt-2 border-t" :class="reading.warnings_max_level ? 'border-orange-200' : 'border-gray-200'">
              <div class="flex items-center gap-1.5">
                <span
                  class="w-2 h-2 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: reading.warnings_max_level ? warningLevelColor(reading.warnings_max_level) : '#22c55e' }"
                />
                <span class="font-semibold" :style="{ color: reading.warnings_max_level ? warningLevelColor(reading.warnings_max_level) : '#16a34a' }">
                  {{ reading.warnings_max_level ? warningLevelLabel(reading.warnings_max_level) : 'Keine Warnung' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <!-- Current weather card -->
      <div class="bg-white rounded-xl shadow p-6">
        <div class="flex items-start justify-between mb-4">
          <h2 class="text-lg font-semibold text-gray-800">
            Aktuell · {{ formatTime(weather.time) }}
          </h2>
          <span class="text-sm text-gray-400">{{ formatDate(weather.time) }}</span>
        </div>
        <table class="w-full text-sm">
          <tbody>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Temperatur</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.temperature, '°C') }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Luftdruck</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.pressure, ' hPa') }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Rel. Feuchte</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.humidity, ' %') }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Taupunkt</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.dewpoint, ' °C') }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Sichtweite</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.visibility, ' km') }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Windrichtung</td>
              <td class="py-2 font-semibold text-right">
                <span v-if="weather.winddirection != null">
                  <span class="inline-block mr-1" :style="{ transform: 'rotate(' + weather.winddirection + 'deg)' }">↑</span>
                  {{ windDirectionLabel(weather.winddirection) }}
                </span>
                <span v-else>–</span>
              </td>
            </tr>
            <tr>
              <td class="py-2 text-gray-500">Windgeschw.</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.windspeed, ' km/h') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Agricultural values card -->
      <div class="bg-white rounded-xl shadow p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Agrar-Kennwerte</h2>
        <table class="w-full text-sm">
          <tbody>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Verdunstung (ET₀)</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.et0, ' mm/Tag') }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Bodentemp. 0 cm</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.soil_temperature, ' °C') }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Bodenfeuchte</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.soil_moisture, ' %') }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Sonnenstunden</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.sunshine_hours, ' h') }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Strahlung</td>
              <td class="py-2 font-semibold text-right">{{ formatVal(weather.radiation, ' W/m²') }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 text-gray-500">Frostrisiko heute</td>
              <td class="py-2 font-semibold text-right" :class="weather.frost_risk ? 'text-blue-600' : 'text-green-600'">
                {{ weather.frost_risk ? 'Ja' : 'Nein' }}
              </td>
            </tr>
            <tr>
              <td class="py-2 text-gray-500">Niederschlag 24h</td>
              <td class="py-2 font-semibold text-right">
                {{ forecast.length ? formatVal(forecast[0].precipitation, ' mm') : '–' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 7-day forecast -->
    <div class="bg-white rounded-xl shadow p-6 mb-6">
      <h2 class="text-lg font-semibold text-gray-800 mb-4">7-Tage-Prognose</h2>
      <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <div
          v-for="(day, i) in forecast"
          :key="day.date"
          class="rounded-lg p-3 text-center border-2 transition-colors"
          :class="i === 0 ? 'border-blue-400 bg-blue-50' : 'border-gray-100'"
        >
          <p class="text-xs text-gray-500 font-medium">{{ i === 0 ? 'Heute' : weekdayName(day.date) }}</p>
          <p class="text-xs text-gray-400 mb-1">{{ shortDate(day.date) }}</p>
          <div class="text-2xl my-2">{{ weatherEmoji(day.weathercode) }}</div>
          <p class="font-bold text-orange-500">{{ roundTemp(day.temp_max) }} °C</p>
          <p class="text-sm text-gray-500">{{ roundTemp(day.temp_min) }} °C</p>
          <p class="text-xs text-blue-400 mt-1">{{ formatVal(day.precipitation, ' mm') }}</p>
        </div>
      </div>
    </div>

    <!-- Warnings legend -->
    <div class="bg-white rounded-xl shadow p-6">
      <h2 class="text-lg font-semibold text-gray-800 mb-3">Warnungen-Legende</h2>
      <div class="flex flex-wrap gap-4 text-sm">
        <span v-for="item in warningLegend" :key="item.label" class="flex items-center gap-2">
          <span class="w-3 h-3 rounded-sm inline-block" :style="{ backgroundColor: item.color }" />
          {{ item.label }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  components: {
    Head,
  },

  layout: Layout,

  props: {
    weather: {
      type: Object,
      default: () => ({}),
    },
    forecast: {
      type: Array,
      default: () => [],
    },
    warnings: {
      type: Array,
      default: () => [],
    },
    location: {
      type: String,
      default: '',
    },
    readings: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      warningLegend: [
        { label: 'Keine Warnung', color: '#22c55e' },
        { label: 'Wetterwarnung', color: '#FFFF00' },
        { label: 'Markantes Wetter', color: '#FF8C00' },
        { label: 'Unwetterwarnung', color: '#FF0000' },
        { label: 'Extremes Unwetter', color: '#8B0000' },
        { label: 'UV-Warnung', color: '#ec4899' },
        { label: 'Hitzewarnung', color: '#a855f7' },
      ],
    }
  },

  methods: {
    formatVal(value, unit) {
      if (value === null || value === undefined) {
        return '–'
      }
      return value + unit
    },

    formatTime(isoString) {
      if (!isoString) {
        return '–'
      }
      const date = new Date(isoString)
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr'
    },

    formatDate(isoString) {
      if (!isoString) {
        return ''
      }
      const date = new Date(isoString)
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },

    weekdayName(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('de-DE', { weekday: 'long' })
    },

    shortDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + '.'
    },

    roundTemp(val) {
      if (val === null || val === undefined) {
        return '–'
      }
      return Math.round(val)
    },

    windDirectionLabel(degrees) {
      const directions = ['N', 'NNO', 'NO', 'ONO', 'O', 'OSO', 'SO', 'SSO', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']
      const index = Math.round(degrees / 22.5) % 16
      return directions[index]
    },

    readingVal(value, unit) {
      if (value === null || value === undefined) {
        return '–'
      }
      return value + ' ' + unit
    },

    readingTime(isoString) {
      if (!isoString) {
        return '–'
      }
      const date = new Date(isoString)
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr'
    },

    readingWeekday(isoString) {
      if (!isoString) {
        return ''
      }
      const date = new Date(isoString)
      return date.toLocaleDateString('de-DE', { weekday: 'long' })
    },

    readingFullDate(isoString) {
      if (!isoString) {
        return ''
      }
      const date = new Date(isoString)
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },

    readingDate(isoString) {
      if (!isoString) {
        return ''
      }
      const date = new Date(isoString)
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + '.'
    },

    warningLevelColor(level) {
      const colors = { 1: '#FFFF00', 2: '#FF8C00', 3: '#FF0000', 4: '#8B0000' }
      return colors[level] || '#FF8C00'
    },

    warningLevelLabel(level) {
      const labels = { 1: 'Wetterwarnung', 2: 'Markantes Wetter', 3: 'Unwetterwarnung', 4: 'Extremes Unwetter' }
      return labels[level] || ''
    },

    warningBannerStyle(warning) {
      return {
        borderColor: warning.color,
        backgroundColor: warning.color + '15',
        color: '#1a1a1a',
      }
    },

    /**
     * Map WMO weather interpretation codes (0–99) to emoji.
     */
    weatherEmoji(code) {
      if (code === null || code === undefined) {
        return '❓'
      }
      const map = {
        0: '☀️',
        1: '🌤️',
        2: '⛅',
        3: '☁️',
        45: '🌫️',
        48: '🌫️',
        51: '🌦️',
        53: '🌦️',
        55: '🌧️',
        56: '🌧️',
        57: '🌧️',
        61: '🌧️',
        63: '🌧️',
        65: '🌧️',
        66: '🌨️',
        67: '🌨️',
        71: '❄️',
        73: '❄️',
        75: '❄️',
        77: '🌨️',
        80: '🌦️',
        81: '🌦️',
        82: '⛈️',
        85: '🌨️',
        86: '🌨️',
        95: '⛈️',
        96: '⛈️',
        99: '⛈️',
      }
      // Return exact match or fall back to range-based lookup
      if (map[code] !== undefined) {
        return map[code]
      }
      if (code <= 3) { return '🌤️' }
      if (code <= 49) { return '🌫️' }
      if (code <= 69) { return '🌧️' }
      if (code <= 79) { return '❄️' }
      if (code <= 84) { return '🌦️' }
      return '⛈️'
    },
  },
}
</script>
